import { useState, useEffect, useCallback, useRef } from 'react';
import type { AppNode, Facets, Meta } from '../types';
import { fetchApps } from '../helpers/api';
import { PARAMS } from '../constants';

/**
 * Filter parameters for the search.
 */
export interface SearchFilters {
  keyword?: string;
  priceType?: number[];
  platforms?: number[];
  condition?: number[];
  features?: number[];
  sortBy?: string;
}

/**
 * Return type for the useAppSearch hook.
 */
export interface UseAppSearchResult {
  apps: AppNode[];
  facets: Facets | null;
  meta: Meta | null;
  loading: boolean;
  error: string | null;
  filters: SearchFilters;
  search: (filters: SearchFilters) => Promise<void>;
  loadMore: () => Promise<void>;
  resetFilters: () => Promise<void>;
}

/**
 * Parse URL search params into SearchFilters.
 */
function parseUrlFilters(): SearchFilters {
  const params = new URLSearchParams(window.location.search);
  const filters: SearchFilters = {};

  const keyword = params.get(PARAMS.KEYWORD);
  if (keyword) filters.keyword = keyword;

  const priceType = params.get(PARAMS.PRICE_TYPE);
  if (priceType) filters.priceType = priceType.split(',').map(Number);

  const platforms = params.get(PARAMS.PLATFORMS);
  if (platforms) filters.platforms = platforms.split(',').map(Number);

  const condition = params.get(PARAMS.CONDITION);
  if (condition) filters.condition = condition.split(',').map(Number);

  const features = params.get(PARAMS.FEATURES);
  if (features) filters.features = features.split(',').map(Number);

  const sortBy = params.get(PARAMS.SORT_BY);
  if (sortBy) filters.sortBy = sortBy;

  return filters;
}

/**
 * Sync filters to URL via replaceState.
 */
function syncUrl(filters: SearchFilters): void {
  const params = new URLSearchParams();

  if (filters.keyword) params.set(PARAMS.KEYWORD, filters.keyword);
  if (filters.priceType?.length)
    params.set(PARAMS.PRICE_TYPE, filters.priceType.join(','));
  if (filters.platforms?.length)
    params.set(PARAMS.PLATFORMS, filters.platforms.join(','));
  if (filters.condition?.length)
    params.set(PARAMS.CONDITION, filters.condition.join(','));
  if (filters.features?.length)
    params.set(PARAMS.FEATURES, filters.features.join(','));
  if (filters.sortBy) params.set(PARAMS.SORT_BY, filters.sortBy);

  const qs = params.toString();
  const url = qs ? `${window.location.pathname}?${qs}` : window.location.pathname;
  window.history.replaceState(null, '', url);
}

/**
 * Build API query string from filters and pagination.
 */
function buildQueryString(
  filters: SearchFilters,
  page: number,
  limit: number
): string {
  const params = new URLSearchParams();

  if (filters.keyword) params.set(PARAMS.KEYWORD, filters.keyword);
  if (filters.priceType?.length)
    params.set(PARAMS.PRICE_TYPE, filters.priceType.join(','));
  if (filters.platforms?.length)
    params.set(PARAMS.PLATFORMS, filters.platforms.join(','));
  if (filters.condition?.length)
    params.set(PARAMS.CONDITION, filters.condition.join(','));
  if (filters.features?.length)
    params.set(PARAMS.FEATURES, filters.features.join(','));
  if (filters.sortBy) params.set(PARAMS.SORT_BY, filters.sortBy);

  params.set(PARAMS.PAGE, String(page));
  params.set(PARAMS.LIMIT, String(limit));

  return params.toString();
}

/**
 * Hook for fetching and managing app library search results.
 */
export function useAppSearch(
  initialLimit: number = 8
): UseAppSearchResult {
  const [apps, setApps] = useState<AppNode[]>([]);
  const [facets, setFacets] = useState<Facets | null>(null);
  const [meta, setMeta] = useState<Meta | null>(null);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState<string | null>(null);
  const [filters, setFilters] = useState<SearchFilters>(parseUrlFilters);
  const [page, setPage] = useState(0);
  const mountedRef = useRef(true);

  useEffect(() => {
    mountedRef.current = true;
    return () => { mountedRef.current = false; };
  }, []);

  const doFetch = useCallback(
    async (searchFilters: SearchFilters, searchPage: number) => {
      setLoading(true);
      setError(null);

      try {
        const qs = buildQueryString(searchFilters, searchPage, initialLimit);
        const data = await fetchApps(qs);

        if (!mountedRef.current) return;

        if (searchPage === 0) {
          setApps(data.data);
        } else {
          setApps((prev) => [...prev, ...data.data]);
        }

        setFacets(data.facets);
        setMeta(data.meta);
      } catch (err) {
        if (!mountedRef.current) return;
        setError(err instanceof Error ? err.message : 'An error occurred');
      } finally {
        if (mountedRef.current) setLoading(false);
      }
    },
    [initialLimit]
  );

  const search = useCallback(
    async (newFilters: SearchFilters) => {
      setFilters(newFilters);
      setPage(0);
      syncUrl(newFilters);
      await doFetch(newFilters, 0);
    },
    [doFetch]
  );

  const loadMore = useCallback(async () => {
    if (meta?.has_more && !loading) {
      const nextPage = page + 1;
      setPage(nextPage);
      await doFetch(filters, nextPage);
    }
  }, [meta, loading, page, filters, doFetch]);

  const resetFilters = useCallback(async () => {
    const empty: SearchFilters = {};
    setFilters(empty);
    setPage(0);
    syncUrl(empty);
    await doFetch(empty, 0);
  }, [doFetch]);

  // Always fetch on mount to populate facets and initial results
  useEffect(() => {
    const fetchInitialResults = async () => {
      try {
        const qs = buildQueryString(filters, 0, initialLimit);
        const data = await fetchApps(qs);

        if (!mountedRef.current) return;

        setApps(data.data);
        setFacets(data.facets);
        setMeta(data.meta);
      } catch (err) {
        if (!mountedRef.current) return;
        setError(err instanceof Error ? err.message : 'An error occurred');
      } finally {
        if (mountedRef.current) setLoading(false);
      }
    };

    void fetchInitialResults();
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, []);

  return { apps, facets, meta, loading, error, filters, search, loadMore, resetFilters };
}
